import React, { memo, useState, useRef } from 'react';
import TaskRow from './TaskRow';
import GanttAddItemPopup from './GanttAddItemPopup';
import { getTaskDates } from '../utils';

/**
 * SwimlaneGroup Component
 * 
 * A collapsible group of tasks in a swimlane.
 * Shows a header with the group name and color, containing task rows.
 */
const SwimlaneGroup = memo(({
  label,
  color,
  tasks,
  lockStates = {},
  viewStartDate,
  totalDays,
  statuses,
  allTags,
  colorBy,
  todayPosition,
  cursorPosition,
  onDateChange,
  onDueDateChange,
  timelineMinWidth,
  selectedTaskIds = new Set(),
  onTaskSelect,
  onTaskToggleSelect,
  onMultiDrag,
  multiDragDelta = 0,
  isDraggingMultiple = false,
  multiDragType = null,
  zoomLevel = 'day',
  // Add item handlers
  onAddTask,
  onAddDecision,
  onAddWait,
  // Decision handlers
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  // Rename handler
  onRenameTask,
  nestedSprints = [],
  allSprints = [],
  // Group key for identifying which swimlane to add to
  groupKey,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const addButtonRef = useRef(null);
  
  // Filter to only tasks with valid dates
  const validTasks = tasks.filter(task => getTaskDates(task));
  
  // Show swimlane even if no valid tasks (so user can add new ones)
  // But only if we have add handlers
  const showEmptyState = validTasks.length === 0 && (onAddTask || onAddDecision || onAddWait);
  
  if (validTasks.length === 0 && !showEmptyState) return null;

  const handleAddClick = (e) => {
    e.stopPropagation();
    setIsAddPopupOpen(!isAddPopupOpen);
  };

  return (
    <div className={`gantt-swimlane ${isCollapsed ? 'gantt-swimlane--collapsed' : ''}`}>
      {/* Swimlane header */}
      <div 
        className="gantt-swimlane__header"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="gantt-swimlane__header-sidebar">
          <span 
            className="gantt-swimlane__chevron"
            style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
          <span 
            className="gantt-swimlane__color-dot"
            style={{ backgroundColor: color }}
          />
          <span className="gantt-swimlane__label">{label}</span>
          <span className="gantt-swimlane__count">{validTasks.length}</span>
          
          {/* Add button */}
          {(onAddTask || onAddDecision || onAddWait) && (
            <button
              ref={addButtonRef}
              className="gantt-swimlane__add-btn"
              onClick={handleAddClick}
              title="Add mission or decision"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          )}
          
          <GanttAddItemPopup
            isOpen={isAddPopupOpen}
            onClose={() => setIsAddPopupOpen(false)}
            buttonRef={addButtonRef}
            swimlaneLabel={label}
            onAddMission={() => onAddTask?.(groupKey, colorBy)}
            onAddDecision={() => onAddDecision?.(groupKey, colorBy)}
            onAddWait={() => onAddWait?.(groupKey, colorBy)}
          />
        </div>
        <div 
          className="gantt-swimlane__header-timeline"
          style={{ minWidth: timelineMinWidth }}
        >
          {/* Today marker in header */}
          {todayPosition !== null && (
            <div 
              className="gantt-today-marker"
              style={{ left: `${todayPosition}%` }}
            />
          )}
          {/* Cursor marker in header */}
          {cursorPosition !== null && (
            <div 
              className="gantt-cursor-marker"
              style={{ left: `${cursorPosition}%` }}
            />
          )}
        </div>
      </div>

      {/* Task rows (collapsible) */}
      {!isCollapsed && (
        <div className="gantt-swimlane__tasks">
          {validTasks.map(task => {
            const taskDates = getTaskDates(task);
            const lockState = lockStates[task.id];
            const isLocked = lockState?.isLocked && task.status !== 'completed';
            return (
              <TaskRow
                key={task.id}
                task={task}
                taskDates={taskDates}
                isLocked={isLocked}
                lockReason={lockState?.lockReason}
                viewStartDate={viewStartDate}
                totalDays={totalDays}
                statuses={statuses}
                allTags={allTags}
                colorBy={colorBy}
                todayPosition={todayPosition}
                cursorPosition={cursorPosition}
                onDateChange={onDateChange}
                onDueDateChange={onDueDateChange}
                timelineMinWidth={timelineMinWidth}
                isSelected={selectedTaskIds.has(task.id)}
                onSelect={onTaskSelect}
                onToggleSelect={onTaskToggleSelect}
                selectedTaskIds={selectedTaskIds}
                onMultiDrag={onMultiDrag}
                multiDragDelta={multiDragDelta}
                isDraggingMultiple={isDraggingMultiple}
                multiDragType={multiDragType}
                zoomLevel={zoomLevel}
                onDecisionAnswerChange={onDecisionAnswerChange}
                onDecisionOptionsChange={onDecisionOptionsChange}
                onRenameTask={onRenameTask}
                nestedSprints={nestedSprints}
                allSprints={allSprints}
              />
            );
          })}
        </div>
      )}
    </div>
  );
});

SwimlaneGroup.displayName = 'SwimlaneGroup';

export default SwimlaneGroup;
